/**
 * @file SectionTabRow.tsx
 * @brief Hairline grid tab row shared by Billing & Pricing and Links & company info.
 *
 * @details
 * The look Billing has had since 30-07-2026, lifted out so a second page can
 * use it unchanged (W20, 25-10-2026). `scroll` keeps every tab on one line
 * and lets the row scroll sideways on a phone (Billing wraps to a grid).
 * `badge` is a small dot (something still to fill) or a count (unsaved).
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { type MouseEvent } from "react";
import type { LucideIcon } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { cn } from "@/shared/theme/cn";

export interface SectionTabItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  /** Unsaved changes on this tab (shown as a count). */
  count?: number;
  /** Something still to fill on this tab (shown as a dot). */
  dot?: boolean;
  /** `end` matching for the NavLink (Billing's Overview). */
  end?: boolean;
}

interface SectionTabRowProps {
  tabs: SectionTabItem[];
  activeId: string;
  ariaLabel: string;
  gridClassName?: string;
  scroll?: boolean;
}

export function SectionTabRow({
  tabs,
  activeId,
  ariaLabel,
  gridClassName = "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
  scroll = false,
}: SectionTabRowProps) {
  const navigate = useNavigate();

  const onNavigate =
    (to: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      navigate(to);
    };

  return (
    <div
      data-sticky-subnav
      className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85"
    >
      <div className={cn(scroll && "overflow-x-auto overscroll-x-contain")}>
        <GridCells
          className={cn(gridClassName, scroll && "grid-flow-col auto-cols-[minmax(max-content,1fr)]")}
          role="tablist"
          aria-label={ariaLabel}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeId === tab.id;
            return (
              <GridCell key={tab.id} padded={false} className="min-w-0">
                <NavLink
                  to={tab.href}
                  end={tab.end}
                  role="tab"
                  aria-selected={active}
                  data-tab={tab.id}
                  onClick={onNavigate(tab.href)}
                  className={cn(
                    "flex h-full min-h-[3.5rem] items-center gap-2.5 px-3 py-3 text-sm transition-colors md:px-4",
                    active
                      ? "bg-brand-soft font-medium text-brand-soft-foreground"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors",
                      active
                        ? "border-primary/25 bg-primary text-primary-foreground"
                        : "border-border bg-background text-muted-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className={cn("min-w-0 leading-tight", scroll ? "whitespace-nowrap" : "truncate")}>
                    {tab.label}
                  </span>
                  {tab.count ? (
                    <span
                      data-tab-count
                      aria-label={`${tab.count} unsaved`}
                      className="shrink-0 rounded-full bg-primary px-1.5 text-xs font-medium leading-5 text-primary-foreground"
                    >
                      {tab.count}
                    </span>
                  ) : null}
                  {tab.dot ? (
                    <span
                      data-tab-dot
                      role="img"
                      aria-label="Still to fill"
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                    />
                  ) : null}
                </NavLink>
              </GridCell>
            );
          })}
        </GridCells>
      </div>
    </div>
  );
}
